import React, { useRef, useState } from "react";
import { X, Store, Phone as PhoneIcon, Camera } from "lucide-react";
import api from "@/api";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";

// Phase C/F (Feb 2026, updated Apr 2026): "Not Bought / No Sale" popup with 5 reasons + custom + MANDATORY photo proof.
// Removed (Apr 2026): `other_company` ("Selling only other company products") and `price_issue` ("Price issue").
// Backend still accepts those codes for legacy data; frontend simply hides them from new entries.
const REASONS = [
  { code: "shop_closed", key: "reason_shop_closed" },
  { code: "already_has_stock", key: "reason_already_has_stock" },
  { code: "owner_absent", key: "reason_owner_absent" },
  { code: "asking_credit", key: "reason_asking_credit" },
  { code: "other", key: "reason_other" },
];

export default function NoSaleDialog({ open, onClose, onSaved, prefill = {} }) {
  const { t, lang } = useLang();
  const fileRef = useRef();
  const [shopName, setShopName] = useState(prefill.shop_name || "");
  const [phone, setPhone] = useState(prefill.phone || "");
  const [reason, setReason] = useState("");
  const [custom, setCustom] = useState("");
  const [photoPath, setPhotoPath] = useState("");
  const [photoPreview, setPhotoPreview] = useState("");
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotoPath(r.data.path);
      setPhotoPreview(URL.createObjectURL(f));
    } catch { toast.error("Upload failed"); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    if (!shopName.trim()) return toast.error(t("enter_shop_name"));
    if (!reason) return toast.error(t("reason"));
    if (reason === "other" && !custom.trim()) return toast.error(t("enter_custom_reason"));
    if (!photoPath) return toast.error(lang === "ta" ? "புகைப்படம் கண்டிப்பா வேணும்" : "Photo proof required");
    // Apr 2026: Attendance gate
    try {
      const att = await api.get("/attendance/today");
      if (!att.data || !att.data.check_in_at) return toast.error(lang === "ta" ? "முதலில் செக் இன் செய்!" : "Please check in first");
      if (att.data.check_out_at) return toast.error(lang === "ta" ? "இன்றைய செக் அவுட் முடிந்தது" : "You have already checked out. Try again tomorrow.");
    } catch { /* network error → allow */ }
    setBusy(true);
    try {
      const payload = {
        shop_name: shopName.trim(),
        phone: phone.trim() || null,
        reason_code: reason,
        custom_reason: reason === "other" ? custom.trim() : null,
        shop_id: prefill.shop_id || null,
        photo_path: photoPath,
      };
      if (navigator.geolocation) {
        try {
          await new Promise((res) => {
            const tmr = setTimeout(res, 1500);
            navigator.geolocation.getCurrentPosition(
              (pos) => { clearTimeout(tmr); payload.geo = { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }; res(); },
              () => { clearTimeout(tmr); res(); },
              { enableHighAccuracy: false, timeout: 1500 }
            );
          });
        } catch {}
      }
      const r = await api.post("/salesman/no-sale", payload);
      toast.success(t("no_sale_saved"));
      onSaved && onSaved(r.data);
      onClose();
      setShopName(""); setPhone(""); setReason(""); setCustom(""); setPhotoPath(""); setPhotoPreview("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] grid place-items-center p-4 bg-black/50 backdrop-blur-sm" data-testid="nosale-dialog">
      <div className="bg-white rounded-3xl w-full max-w-md max-h-[90vh] overflow-y-auto fade-up">
        <div className="sticky top-0 bg-white border-b border-gray-100 p-5 flex items-center justify-between">
          <div>
            <div className="heading-font text-xl font-bold text-[#1D1D1F]">{t("not_bought")}</div>
            <div className="text-xs text-[#86868B]">{lang === "ta" ? "காரணத்தை தேர்ந்தெடுக்கவும்" : "Pick a reason"}</div>
          </div>
          <button onClick={onClose} data-testid="nosale-close" className="w-9 h-9 grid place-items-center rounded-full hover:bg-gray-100">
            <X className="w-5 h-5 text-[#86868B]" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="space-y-2">
            <label className="text-[10px] uppercase tracking-wider text-[#86868B] font-bold flex items-center gap-1.5">
              <Store className="w-3.5 h-3.5" /> {t("shop_name")}
            </label>
            <input
              data-testid="nosale-shop-name"
              type="text"
              value={shopName}
              onChange={(e) => setShopName(e.target.value)}
              style={{ color: "#0A1A3F", WebkitTextFillColor: "#0A1A3F" }}
              className="w-full bg-[#F5F5F7] rounded-2xl px-4 py-3 text-base font-semibold focus:bg-white focus:ring-2 focus:ring-[#0055FF]/30 outline-none"
            />
          </div>
          <div className="space-y-2">
            <label className="text-[10px] uppercase tracking-wider text-[#86868B] font-bold flex items-center gap-1.5">
              <PhoneIcon className="w-3.5 h-3.5" /> {t("phone")}
            </label>
            <input
              data-testid="nosale-phone"
              type="tel"
              inputMode="numeric"
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
              style={{ color: "#0A1A3F", WebkitTextFillColor: "#0A1A3F" }}
              className="w-full bg-[#F5F5F7] rounded-2xl px-4 py-3 text-base font-semibold focus:bg-white focus:ring-2 focus:ring-[#0055FF]/30 outline-none"
            />
          </div>

          <div className="space-y-2">
            <div className="text-[10px] uppercase tracking-wider text-[#86868B] font-bold">{t("reason")}</div>
            <div className="grid grid-cols-1 gap-2">
              {REASONS.map(r => (
                <button
                  key={r.code}
                  data-testid={`nosale-reason-${r.code}`}
                  onClick={() => setReason(r.code)}
                  className={`text-left rounded-2xl px-4 py-3 font-semibold border transition ${reason === r.code ? "bg-[#0055FF] text-white border-[#0055FF] shadow-md" : "bg-white text-[#1D1D1F] border-gray-200 hover:bg-gray-50"}`}
                >
                  {t(r.key)}
                </button>
              ))}
            </div>
          </div>

          {reason === "other" && (
            <div className="space-y-2 fade-up">
              <textarea
                data-testid="nosale-custom"
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
                placeholder={t("enter_custom_reason")}
                rows={3}
                style={{ color: "#0A1A3F", WebkitTextFillColor: "#0A1A3F" }}
                className="w-full bg-[#F5F5F7] rounded-2xl px-4 py-3 text-base focus:bg-white focus:ring-2 focus:ring-[#0055FF]/30 outline-none resize-none"
              />
            </div>
          )}

          {/* MANDATORY photo proof */}
          <div className="space-y-2">
            <div className="text-[10px] uppercase tracking-wider text-[#86868B] font-bold flex items-center gap-1.5">
              <Camera className="w-3.5 h-3.5" /> {lang === "ta" ? "புகைப்படம் (கட்டாயம்)" : "Photo Proof (required)"}
            </div>
            <input ref={fileRef} type="file" accept="image/*" capture="environment" hidden onChange={onPhoto} data-testid="nosale-photo-input" />
            {!photoPreview ? (
              <button
                data-testid="nosale-take-photo"
                onClick={() => fileRef.current?.click()}
                className="w-full rounded-2xl py-5 border-dashed border-2 border-[#0055FF]/30 hover:border-[#0055FF] hover:bg-blue-50/30 text-[#0055FF] font-bold flex items-center justify-center gap-2 transition"
              >
                <Camera className="w-5 h-5" /> {lang === "ta" ? "கேமராவை திற" : "Take Photo"}
              </button>
            ) : (
              <div className="relative rounded-2xl overflow-hidden bg-gray-100" data-testid="nosale-photo-preview">
                <img src={photoPreview} alt="proof" className="w-full max-h-56 object-cover" />
                <button onClick={() => fileRef.current?.click()} className="absolute bottom-2 right-2 bg-white/95 px-3 py-1.5 rounded-xl text-sm font-bold shadow">
                  {lang === "ta" ? "மறுபடி" : "Retake"}
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-gray-100 p-4">
          <button
            data-testid="nosale-submit"
            disabled={busy}
            onClick={submit}
            className="w-full bg-[#34C759] hover:bg-[#2EAF4E] disabled:opacity-50 text-white rounded-2xl py-3.5 font-bold shadow-md"
          >
            {t("save")}
          </button>
        </div>
      </div>
    </div>
  );
}
